import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/features/platform/contexts/auth-context";

/** Where "Back to platform" goes: the management console for platform admins, else the tenant portal. */
const usePlatformHome = () => {
  const { user } = useAuth();
  const isPlatformUser = Boolean(
    user?.admin || user?.roles?.some((r) => r.scope === "platform")
  );
  return isPlatformUser || !user?.tenant_slug ? "/m" : `/t/${user.tenant_slug}`;
};

export const DocsUserMenu = () => {
  const { user, logout } = useAuth();
  const platformHome = usePlatformHome();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!user) return null;
  const name = user.name || user.email || "User";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white ring-2 ring-white transition-shadow hover:shadow-md dark:ring-slate-950"
        title={user.email}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {name.charAt(0).toUpperCase()}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">{name}</p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
          </div>
          <div className="py-1">
            <Link
              to={platformHome}
              onClick={() => setOpen(false)}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <ArrowLeft className="h-4 w-4 text-slate-400" />
              <span>Back to platform</span>
            </Link>
          </div>
          <div className="border-t border-slate-200 py-1 dark:border-slate-700">
            <button
              onClick={logout}
              role="menuitem"
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/10"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
